// ABOUTME: Enhanced authentication guard with security features
// ABOUTME: Includes rate limiting, input validation, and security monitoring

import { ReactNode, useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { useSecurityAudit } from '@/hooks/useSecurityAudit';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/components/ui/use-toast';
import { validateEmail, validatePassword, sanitizeName } from '@/utils/validation';
import PasswordStrengthIndicator from './PasswordStrengthIndicator';
import ForgotPasswordForm from './ForgotPasswordForm';
import { AlertTriangle, Shield, Lock, Eye, EyeOff } from 'lucide-react';
interface SecureAuthGuardProps {
  children: ReactNode;
}
export default function SecureAuthGuard({
  children
}: SecureAuthGuardProps) {
  const {
    user,
    loading
  } = useAuth();
  const {
    logLoginAttempt
  } = useSecurityAudit();
  const [isSignUp, setIsSignUp] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [failedAttempts, setFailedAttempts] = useState(0);
  const [isLocked, setIsLocked] = useState(false);
  const [lockoutTimer, setLockoutTimer] = useState(0);
  const [showPassword, setShowPassword] = useState(false);

  // Enhanced rate limiting - lockout after 5 failed attempts for 15 minutes
  const MAX_ATTEMPTS = 5;
  const LOCKOUT_DURATION = 15 * 60 * 1000; // 15 minutes
  const SESSION_TIMEOUT = 60 * 60 * 1000; // 1 hour of inactivity

  // Create a stable reset timeout function using useCallback
  const resetTimeout = useCallback(() => {
    const timeout = setTimeout(() => {
      toast({
        title: "Session Expired",
        description: "Your session has expired due to inactivity. Please log in again.",
        variant: "destructive"
      });
      supabase.auth.signOut();
    }, SESSION_TIMEOUT);
    return timeout;
  }, []);

  // Session timeout management - fixed infinite loop issue
  useEffect(() => {
    if (user) {
      let currentTimeout: NodeJS.Timeout;
      const handleActivity = () => {
        if (currentTimeout) clearTimeout(currentTimeout);
        currentTimeout = resetTimeout();
      };

      // Listen for user activity
      const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart'];
      events.forEach(event => {
        document.addEventListener(event, handleActivity, {
          passive: true
        });
      });

      // Set initial timeout
      currentTimeout = resetTimeout();
      return () => {
        if (currentTimeout) clearTimeout(currentTimeout);
        events.forEach(event => {
          document.removeEventListener(event, handleActivity);
        });
      };
    }
  }, [user, resetTimeout]);

  // Lockout timer management
  useEffect(() => {
    if (isLocked && lockoutTimer > 0) {
      const timer = setInterval(() => {
        setLockoutTimer(prev => {
          if (prev <= 1000) {
            setIsLocked(false);
            setFailedAttempts(0);
            return 0;
          }
          return prev - 1000;
        });
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [isLocked, lockoutTimer]);
  const passwordValidation = validatePassword(password);
  if (loading) {
    return <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading secure session...</p>
        </div>
      </div>;
  }
  if (!user) {
    if (showForgotPassword) {
      return <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100">
          <ForgotPasswordForm onBack={() => setShowForgotPassword(false)} />
        </div>;
    }
    const handleAuth = async (e: React.FormEvent) => {
      e.preventDefault();
      if (isLocked) {
        toast({
          title: "Account Temporarily Locked",
          description: `Too many failed attempts. Try again in ${Math.ceil(lockoutTimer / 60000)} minutes.`,
          variant: "destructive"
        });
        return;
      }

      // Enhanced input validation
      if (!validateEmail(email)) {
        toast({
          title: "Invalid Email",
          description: "Please enter a valid email address.",
          variant: "destructive"
        });
        return;
      }
      if (isSignUp && !passwordValidation.isValid) {
        toast({
          title: "Password Security Requirements Not Met",
          description: "Please ensure your password meets all security requirements.",
          variant: "destructive"
        });
        return;
      }
      setIsLoading(true);
      console.log('Starting secure authentication process...', {
        isSignUp,
        email
      });
      try {
        if (isSignUp) {
          const sanitizedFirstName = sanitizeName(firstName);
          const sanitizedLastName = sanitizeName(lastName);
          if (!sanitizedFirstName || !sanitizedLastName) {
            throw new Error("First name and last name are required and must contain valid characters.");
          }
          console.log('Attempting secure signup with data:', {
            email,
            firstName: sanitizedFirstName,
            lastName: sanitizedLastName
          });
          const {
            data,
            error
          } = await supabase.auth.signUp({
            email,
            password,
            options: {
              data: {
                first_name: sanitizedFirstName,
                last_name: sanitizedLastName
              },
              emailRedirectTo: `${window.location.origin}/`
            }
          });
          if (error) {
            console.error('Secure signup error:', error);
            logLoginAttempt(false, error.message);
            throw error;
          }
          console.log('Secure signup successful:', data);
          logLoginAttempt(true);
          toast({
            title: "Account Created Successfully!",
            description: "Please check your email to confirm your account before signing in."
          });

          // Reset form and switch to login
          setFailedAttempts(0);
          setIsSignUp(false);
          setPassword('');
        } else {
          console.log('Attempting secure login with email:', email);
          const {
            data,
            error
          } = await supabase.auth.signInWithPassword({
            email,
            password
          });
          if (error) {
            console.error('Secure login error:', error);

            // Enhanced failed attempt handling
            const newAttempts = failedAttempts + 1;
            setFailedAttempts(newAttempts);
            logLoginAttempt(false, error.message);
            if (newAttempts >= MAX_ATTEMPTS) {
              setIsLocked(true);
              setLockoutTimer(LOCKOUT_DURATION);
              toast({
                title: "Account Security Lock",
                description: `Account temporarily locked due to multiple failed attempts. Please wait 15 minutes before trying again.`,
                variant: "destructive"
              });
            } else {
              toast({
                title: "Authentication Failed",
                description: `Invalid credentials. ${MAX_ATTEMPTS - newAttempts} attempts remaining before temporary lock.`,
                variant: "destructive"
              });
            }
            throw error;
          }
          console.log('Secure login successful:', data);
          logLoginAttempt(true);

          // Reset security counters on successful login
          setFailedAttempts(0);
          toast({
            title: "Welcome Back!",
            description: "You have been successfully authenticated."
          });
        }
      } catch (error: any) {
        console.error('Authentication error:', error);

        // Enhanced error handling
        let errorMessage = error.message;
        if (error.message?.includes('Database error saving new user')) {
          errorMessage = 'Account creation temporarily unavailable. Please try again in a moment.';
        } else if (error.message?.includes('User already registered')) {
          errorMessage = 'An account with this email already exists. Please sign in instead.';
        } else if (error.message?.includes('Invalid login credentials')) {
          errorMessage = 'Invalid email or password. Please verify your credentials.';
        } else if (error.message?.includes('Email not confirmed')) {
          errorMessage = 'Please check your email and confirm your account before signing in.';
        }
        if (!isSignUp || !error.message?.includes('failed login attempts')) {
          toast({
            title: "Authentication Error",
            description: errorMessage,
            variant: "destructive"
          });
        }
      } finally {
        setIsLoading(false);
      }
    };
    return <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 bg-slate-50">
        <Card className="w-full max-w-md shadow-2xl border-0">
          <CardHeader className="text-center bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-t-lg">
            <CardTitle className="flex items-center gap-2 justify-center text-3xl text-white">
              <Shield className="w-6 h-6" />
              Sourcerer AI
            </CardTitle>
            <p className="text-white/90 text-sm mt-2">High quality Job candidates through Referrals</p>
          </CardHeader>
          <CardContent className="p-6">
            {isLocked && <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-3">
                <Lock className="w-5 h-5 text-red-600 flex-shrink-0" />
                <div className="text-sm text-red-700">
                  <div className="font-medium">Security Lock Active</div>
                  <div>Account locked for {Math.ceil(lockoutTimer / 60000)} minutes due to multiple failed attempts.</div>
                </div>
              </div>}

            {failedAttempts > 0 && !isLocked && <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-amber-600" />
                <div className="text-sm text-amber-700">
                  <strong>Security Notice:</strong> {failedAttempts} failed attempt{failedAttempts > 1 ? 's' : ''}. 
                  {MAX_ATTEMPTS - failedAttempts} remaining before temporary lock.
                </div>
              </div>}

            <form onSubmit={handleAuth} className="space-y-4">
              {isSignUp && <>
                  <div>
                    <Label htmlFor="firstName" className="text-sm font-medium">First Name</Label>
                    <Input id="firstName" type="text" value={firstName} onChange={e => setFirstName(e.target.value)} maxLength={50} pattern="[a-zA-Z\s\-']+" title="Only letters, spaces, hyphens, and apostrophes allowed" className="mt-1" required />
                  </div>
                  <div>
                    <Label htmlFor="lastName" className="text-sm font-medium">Last Name</Label>
                    <Input id="lastName" type="text" value={lastName} onChange={e => setLastName(e.target.value)} maxLength={50} pattern="[a-zA-Z\s\-']+" title="Only letters, spaces, hyphens, and apostrophes allowed" className="mt-1" required />
                  </div>
                </>}
              
              <div>
                <Label htmlFor="email" className="text-sm font-medium">Email Address</Label>
                <Input id="email" type="email" value={email} onChange={e => setEmail(e.target.value)} className={`mt-1 ${!validateEmail(email) && email ? 'border-red-500' : ''}`} placeholder="Enter your email address" required />
                {email && !validateEmail(email) && <p className="text-sm text-red-600 mt-1">Please enter a valid email address</p>}
              </div>
              
              <div>
                <Label htmlFor="password" className="text-sm font-medium">Password</Label>
                <div className="relative mt-1">
                  <Input 
                    id="password" 
                    type={showPassword ? "text" : "password"} 
                    value={password} 
                    onChange={e => setPassword(e.target.value)} 
                    className={`pr-10 ${isSignUp && !passwordValidation.isValid && password ? 'border-red-500' : ''}`} 
                    placeholder={isSignUp ? "Create a strong password" : "Enter your password"} 
                    minLength={isSignUp ? 8 : undefined} 
                    required 
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                {isSignUp && password && <PasswordStrengthIndicator password={password} errors={passwordValidation.errors} strength={passwordValidation.strength} />}
              </div>
              
              <Button type="submit" className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-medium py-2 px-4 rounded-md transition-all duration-200" disabled={isLoading || isLocked || isSignUp && !passwordValidation.isValid}>
                {isLoading ? 'Processing...' : isSignUp ? 'Create Account' : 'Sign In'}
              </Button>
            </form>
            
            <div className="mt-6 text-center">
              <Button variant="link" onClick={() => {
              setIsSignUp(!isSignUp);
              setPassword('');
              setFailedAttempts(0);
            }} className="text-sm text-blue-600 hover:text-blue-800" disabled={isLocked}>
                {isSignUp ? 'Already have an account? Sign in' : "Don't have an account? Sign up"}
              </Button>
            </div>

            {!isSignUp && <div className="mt-2 text-center">
                <Button variant="link" onClick={() => setShowForgotPassword(true)} className="text-sm text-blue-600 hover:text-blue-800" disabled={isLocked}>
                  Forgot your password?
                </Button>
              </div>}

            <div className="mt-4 text-center text-xs text-gray-500">
              
            </div>
          </CardContent>
        </Card>
      </div>;
  }
  return <>{children}</>;
}